/** Thin client over the Duly API. The bearer token is the only local state. */

const TOKEN_KEY = "duly.token";
const MEMBER_KEY = "duly.member";

export interface Member {
  id: string;
  householdId: string | null;
  displayName: string;
  uiLanguage: string;
  speechLocale: string;
  timezone: string;
  assistConsent: string;
}

export interface Household {
  id: string;
  name: string;
  locale: { country: string; region: string | null; timezone: string; displayCurrency: string };
  assist: { level: string };
  settings: { calmMode: boolean; quietHours: { start: string; end: string } };
}

export interface Enrichment {
  explanation: string | null;
  subtasks: { title: string; accepted: boolean }[];
  prep: string[];
  openQuestion: string | null;
  effort: string | null;
  context: string[];
}

export interface Task {
  _id: string;
  title: string;
  notes: string | null;
  /** null means "either of us" — the For all tab. */
  ownerId: string | null;
  requesterId: string;
  status: string;
  dueAt: string | null;
  dueIsDateOnly: boolean;
  trigger: string | null;
  recurrence: string | null;
  category: string | null;
  snoozeReason: string | null;
  declineReason: string | null;
  source: string;
  rawInput: string | null;
  aiFieldOrigin: Record<string, string>;
  /** Tier-2 proposals. Never applied to the fields above — see docs/06 §1. */
  enrichment: Enrichment | null;
  completedAt: string | null;
  completedBy: string | null;
  createdAt: string;
  version: number;
}

export interface TaskEvent {
  _id: string;
  taskId: string;
  actorId: string;
  kind: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export type SectionKey =
  | "needs_you"
  | "waiting_on"
  | "for_all"
  | "coming_up"
  | "no_date"
  | "done_today";
export type Sections = Record<SectionKey, Task[]>;

/** Matches the server's SORTS. */
export const SORTS = ["due", "added", "title", "label"] as const;
export type Sort = (typeof SORTS)[number];

/**
 * The label vocabulary, mirroring @duly/shared LABEL_ORDER and LABEL_COPY.
 *
 * The client keeps its own copy rather than importing the zod enum, because the
 * display names and one-liners are UI copy and the server has no business
 * owning them. The ORDER and the KEYS are the contract; a test on the server
 * side guards the keys.
 */
export const LABELS = [
  { id: "health", name: "Health", sub: "Appointments, prescriptions, the insurer" },
  { id: "admin", name: "Admin", sub: "Contracts, forms, deadlines, the authorities" },
  { id: "finance", name: "Money", sub: "Bills, transfers, anything with a due amount" },
  { id: "home", name: "Home", sub: "The flat itself — repairs, bins, cleaning" },
  { id: "social", name: "People", sub: "Birthdays, visits, presents, calls" },
  { id: "errand", name: "Errand", sub: "Something to buy, fetch or drop off" },
] as const;
export type LabelId = (typeof LABELS)[number]["id"];
export const labelName = (id: string | null): string =>
  LABELS.find((l) => l.id === id)?.name ?? "";

export interface CalendarView {
  /** Keyed by local YYYY-MM-DD. */
  days: Record<string, Task[]>;
  undated: Task[];
  timezone: string;
  today: string;
  serverTime: string;
}

/** The sentinel the server understands for "nobody in particular". */
export const ANYONE = "anyone";

export const token = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(MEMBER_KEY);
  },
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const t = token.get();
  const res = await fetch(path, {
    ...init,
    headers: {
      // Only declare a JSON body when there IS one. Sending
      // content-type: application/json with an empty body makes Fastify try to
      // parse nothing and reject the request — which is what broke DELETE.
      ...(init.body != null ? { "content-type": "application/json" } : {}),
      ...(t ? { authorization: `Bearer ${t}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string }).error ?? "error");
  return body as T;
}

const post = <T,>(p: string, b?: unknown) =>
  call<T>(p, { method: "POST", body: JSON.stringify(b ?? {}) });

/**
 * Where you ARE, not what language your browser is in.
 *
 * The browser's language tag was the only signal, and it is the wrong one: an
 * English-language browser in Munich reported GB while the clock said
 * Europe/Berlin, and country is what decides currency, holidays and how a
 * notice period is read. The time zone is the better answer whenever it names
 * a place we know; the language tag is the fallback.
 */
const TZ_COUNTRY: Record<string, string> = {
  "Europe/Berlin": "DE",
  "Europe/Busingen": "DE",
  "Europe/Vienna": "AT",
  "Europe/Zurich": "CH",
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Paris": "FR",
  "Europe/Amsterdam": "NL",
  "Europe/Brussels": "BE",
  "Europe/Madrid": "ES",
  "Europe/Rome": "IT",
  "Europe/Lisbon": "PT",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Copenhagen": "DK",
  "Europe/Stockholm": "SE",
  "Europe/Oslo": "NO",
  "Europe/Helsinki": "FI",
};

export function guessLocale(): {
  country: string;
  region: null;
  timezone: string;
  weekStart: number;
  displayCurrency: string;
} {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
  const country =
    TZ_COUNTRY[timezone] ?? (navigator.language.split("-")[1] ?? "DE").toUpperCase();
  return {
    country,
    region: null,
    timezone,
    // Monday. Every country in the list above starts the week on Monday, and a
    // household can say otherwise in settings.
    weekStart: 1,
    displayCurrency: country === "CH" ? "CHF" : country === "GB" ? "GBP" : "EUR",
  };
}

export const api = {
  health: () => call<{ tier1: "model" | "local" }>("/v1/health"),

  async registerDevice(): Promise<void> {
    // No account, no password. A device identity is created on first visit and
    // the opaque token is all that is ever stored.
    const r = await post<{ memberId: string; token: string }>("/v1/devices", {
      publicKey: crypto.randomUUID(),
      platform: "web",
    });
    token.set(r.token);
  },

  me: () => call<{ member: Member; household: Household | null; members: Member[] }>("/v1/me"),

  /** Mine alone. Nobody else in the household can change these. */
  updateMe: (patch: Record<string, unknown>) =>
    call<{ member: Member }>("/v1/me", { method: "PATCH", body: JSON.stringify(patch) }),

  /** Shared. Either partner changes them for both. */
  updateHousehold: (patch: Record<string, unknown>) =>
    call<{ household: Household }>("/v1/household", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** A code that carries THIS account to another device. */
  transferCode: () => post<{ code: string; expiresAt: string }>("/v1/devices/transfer"),

  /** Redeem one on the new device. Returns a token for the SAME member. */
  transferIn: (code: string) =>
    post<{ memberId: string; token: string }>(
      `/v1/devices/transfer/${encodeURIComponent(code)}`,
    ),

  createHousehold: (name: string, displayName: string) =>
    post<{ householdId: string }>("/v1/households", {
      name,
      displayName,
      locale: guessLocale(),
    }),

  createInvite: () =>
    post<{ code: string; url: string; expiresAt: string }>("/v1/invites"),

  redeem: (code: string, displayName: string) =>
    post<{ householdId: string }>(
      `/v1/invites/${encodeURIComponent(code)}/redeem`,
      { displayName },
    ),

  dashboard: (sort: Sort = "due") =>
    call<{ sections: Sections; sort: Sort; serverTime: string }>(
      `/v1/tasks?view=dashboard&sort=${sort}`,
    ),

  /** Everything with a date, grouped by local day, plus what has no date. */
  calendar: () => call<CalendarView>("/v1/tasks?view=calendar"),

  /** Public by design — it is the server's identity to the push service. */
  pushKey: () => call<{ enabled: boolean; publicKey: string | null }>("/v1/push/key"),
  pushSubscribe: (sub: PushSubscriptionJSON) => post<{ ok: true }>("/v1/push/subscribe", sub),
  pushUnsubscribe: (endpoint: string) =>
    call<void>("/v1/push/unsubscribe", {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    }),
  pushDevices: () =>
    call<{ devices: { id: string; userAgent: string | null; lastOkAt: string | null }[] }>(
      "/v1/push/devices",
    ),

  parse: (transcript: string, lang?: "en" | "de") =>
    post<{
      title: string;
      ownerId: string | null;
      dueAt: string | null;
      dueIsDateOnly: boolean;
      trigger: string | null;
      recurrence: string | null;
      category: string | null;
      isFollowUp: boolean;
      tier: 0 | 1;
      fallbackReason?: string;
    }>("/v1/parse", { transcript, lang: lang ?? (navigator.language.startsWith("de") ? "de" : "en") }),

  createTask: (t: Partial<Task> & { title: string }) => post<Task>("/v1/tasks", t),
  task: (id: string) => call<Task>(`/v1/tasks/${id}`),
  events: (id: string) => call<{ events: TaskEvent[] }>(`/v1/tasks/${id}/events`),
  act: (id: string, action: string, body?: unknown) =>
    post<Task | { ok: true }>(`/v1/tasks/${id}/${action}`, body),
  remove: (id: string) => call<void>(`/v1/tasks/${id}`, { method: "DELETE" }),

  /** Edit the fields a human owns — notes, title, date. */
  patch: (id: string, fields: Record<string, unknown>) =>
    call<Task>(`/v1/tasks/${id}`, { method: "PATCH", body: JSON.stringify(fields) }),

  /** Move a task between the three tabs. `to` is a member id or ANYONE. */
  assign: (id: string, to: string) => post<Task>(`/v1/tasks/${id}/assign`, { to }),

  /** Upgrade an already-saved task with the tier-1 parse. */
  applyParse: (id: string, p: Record<string, unknown>) =>
    post<Task>(`/v1/tasks/${id}/apply-parse`, p),

  /** Tier 2 on demand. The user asking is always a valid reason to run it. */
  enrich: (id: string) => post<Task>(`/v1/tasks/${id}/enrich`),
  subtask: (id: string, title: string, accept: boolean) =>
    post<unknown>(`/v1/tasks/${id}/subtask`, { title, accept }),
};
